import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { Theme, useTheme } from '@mui/material/styles';
import { sortBy } from 'es-toolkit/compat';
import React, { useEffect, useState } from 'react';
import { SketchPicker } from 'react-color';

import { defaultLayerColors } from 'components/theme/theme';
import { getColorHex } from 'utils/helpers';

export const getColorSwatchButtonStyles = (theme: Theme, background: string) => ({
  background,
  boxShadow: `0 0 0 1px ${theme.palette.action.active}`,
  '&:hover': {
    background,
    boxShadow: `0 0 0 1px ${theme.palette.action.active}, 0 0 0 3px ${theme.palette.action.hover}`,
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
});

export const ColorPickerButton: React.FC<
  React.PropsWithChildren<{
    hexColor?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    disable?: boolean;
    sx?: IconButtonProps['sx'];
  }>
> = ({ hexColor, onClick, disable = false, sx }) => {
  const disableEditing = !onClick;
  const theme = useTheme();
  return (
    <IconButton
      role="color-picker"
      size="small"
      disableRipple
      sx={{
        ...sx,
        minWidth: 0,
        width: 17,
        height: 17,
        p: 0,
        mr: 1,
        display: 'flex',
        borderRadius: '50%',
        ...(!disable && hexColor ? getColorSwatchButtonStyles(theme, hexColor) : {}),
        ...(disableEditing && !disable && hexColor // If we only disable editing, we want to show the color but not allow editing
          ? {
              '&.Mui-disabled': {
                backgroundColor: hexColor,
              },
            }
          : {}),
      }}
      disabled={disable || disableEditing}
      onClick={onClick}
    />
  );
};

export const ColorPicker: React.FC<
  React.PropsWithChildren<{
    currentColor: any;
    onColorChange?: (color: string) => void;
    disable?: boolean;
    disableAlpha?: boolean;
    disableEditing?: boolean; // Still shows the color picker but disables editing
    applyChangeWhenFocusOut?: boolean; // call the onColorChange only when the user clicks save
    buttonSx?: IconButtonProps['sx'];
  }>
> = ({
  currentColor,
  onColorChange,
  disable = false,
  disableAlpha = false,
  disableEditing = false,
  applyChangeWhenFocusOut = false,
  buttonSx,
}) => {
  const theme = useTheme();
  if (!currentColor) {
    currentColor = theme.palette.primary.main;
  }
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [temporaryColor, setTemporaryColor] = useState(currentColor);

  useEffect(() => {
    setTemporaryColor(currentColor);
  }, [currentColor]);

  const colorToShow = applyChangeWhenFocusOut ? temporaryColor : currentColor;

  const openColorPopOver = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const closeColorPopOver = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (applyChangeWhenFocusOut && reason === 'backdropClick' && temporaryColor !== currentColor) {
      onColorChange(temporaryColor);
    } else if (reason !== 'backdropClick') {
      setTemporaryColor(currentColor);
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const hexColor = getColorHex(colorToShow);

  return (
    <>
      <ColorPickerButton
        hexColor={hexColor}
        onClick={!disableEditing ? openColorPopOver : undefined}
        disable={disable}
        sx={buttonSx}
      />
      <Popover
        onClick={(event) => {
          event.stopPropagation();
        }}
        open={open && !disable && !disableEditing}
        anchorEl={anchorEl}
        onClose={closeColorPopOver}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{
          '& .sketch-picker': {
            backgroundColor: 'inherit !important',
          },
        }}
      >
        <SketchPicker
          color={colorToShow}
          disableAlpha={disableAlpha}
          onChange={(color) => {
            if (!onColorChange) console.error('ColorPicker: onColorChange is required when not disabled');
            else if (!applyChangeWhenFocusOut && color?.hex !== currentColor) onColorChange(color?.hex);
            else setTemporaryColor(color?.hex);
          }}
          presetColors={sortBy(defaultLayerColors)}
        />
      </Popover>
    </>
  );
};
